import React, { createContext, useContext, useState, useEffect } from "react";

export type Language = "en" | "de" | "it";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => any;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within a LanguageProvider");
  return context;
};

const TRANSLATIONS: Record<Language, Record<string, any>> = {
  it: {
    common: {
      analysisFor: "Analisi per",
      viewProfile: "Vedi Profilo LinkedIn",
      copy: "Copia",
      openNewTab: "Apri in nuova scheda",
      poweredBy: "Embedded AI Studio powered by Giovanni Burdi",
      connectLinkedIn: "Connettiti su LinkedIn",
      followLinkedIn: "Segui su LinkedIn",
      subscribeLinkedIn: "Iscriviti su LinkedIn",
      goNewsletter: "Vai alla Newsletter LinkedIn",
      openManual: "Leggi Manuale Utente",
      downloadManual: "Scarica PDF",
      close: "Chiudi",
      zoomIn: "Ingrandisci",
      zoomOut: "Rimpicciolisci",
    },
    nav: {
      optimizer: "Optimizer",
      brew: "360Brew",
      giovanni: "Giovanni Burdi",
      roi: "The ROI Architect",
      viewpoint: "The Viewpoint",
      ridmix: "Ridmix Music",
      studio: "Creative Studio",
    },
    home: {
      title: "LinkedIn Profile",
      subtitle: "360Brew Optimizer",
      description: "Analizza il tuo profilo LinkedIn sezione per sezione secondo l'algoritmo AI 360Brew. Ottieni suggerimenti pronti da copiare per raggiungere il 95%+ di score.",
      inputTitle: "Inserisci i dati del tuo profilo",
      fullNameLabel: "Nome Completo",
      profileUrlLabel: "Link Profilo LinkedIn",
      headlineLabel: "Headline",
      aboutLabel: "Sezione About",
      skillsLabel: "Competenze (Skills)",
      industryLabel: "Settore",
      positionLabel: "Ruolo Attuale",
      experienceLabel: "Esperienza Lavorativa (Dettagli)",
      experienceTitleLabel: "Qualifica / Titolo",
      experienceTypeLabel: "Tipo di Impiego",
      experienceCompanyLabel: "Azienda / Organizzazione",
      experienceDescLabel: "Descrizione",
      experienceSkillsLabel: "Competenze (Skills utilizzate)",
      servicesBioLabel: "Bio dei Servizi (Max 500 car.)",
      servicesListLabel: "Lista dei Servizi (separati da virgola)",
      analyzeBtn: "Avvia Analisi 360Brew",
      analyzing: "Analisi in corso...",
      resetBtn: "Reset",
      overallScore: "Score Globale 360Brew",
      copyBtn: "Copia Testo Ottimizzato",
      copied: "Copiato!",
      analysisTitle: "Analisi",
      suggestionsTitle: "Suggerimenti per 95%+",
      optimizedTitle: "Testo Ottimizzato (Pronto da Copiare)",
      placeholderFullName: "Es: Giovanni Burdi",
      placeholderProfileUrl: "Es: https://www.linkedin.com/in/username",
      placeholderHeadline: "Es: B2B SaaS Growth Marketer | AI Strategy | GTM Expert | 10+ anni",
      placeholderAbout: "Incolla qui la tua sezione About da LinkedIn...",
      placeholderSkills: "Es: Marketing Strategy, SEO, AI, Leadership...",
      placeholderIndustry: "Es: Tecnologia, Marketing, SaaS...",
      placeholderPosition: "Es: Growth Manager",
      placeholderExperienceTitle: "Es: Senior Growth Manager",
      placeholderExperienceType: "Es: Full-time, Freelance, Part-time",
      placeholderExperienceCompany: "Es: Tech Corp",
      placeholderExperienceDesc: "Incolla qui la descrizione del ruolo...",
      placeholderExperienceSkills: "Es: Technical SEO, AI, GTM Strategy...",
      placeholderServicesBio: "Descrivi i tuoi servizi in modo persuasivo e denso semanticamente...",
      placeholderServicesList: "Es: Growth Strategy, Technical SEO, AI Automation...",
      errorInput: "Inserisci almeno Headline e About per avviare l'analisi",
      successAnalysis: "Analisi completata! Score 360Brew: ",
      ctaTitle: "Vuoi un'analisi più profonda?",
      ctaDesc: "Connettiti con Giovanni Burdi per strategie GTM avanzate.",
      ctaBtn: "Connettiti su LinkedIn",
    },
    sections: {
      headline: {
        label: "Headline",
        description: "Il tuo biglietto da visita — prima cosa che 360Brew valuta",
      },
      about: {
        label: "About",
        description: "Il tuo storytelling professionale e narrativa semantica",
      },
      skills: {
        label: "Competenze",
        description: "Le skills dichiarate e la loro coerenza tematica",
      },
      coherence: {
        label: "Coerenza",
        description: "Allineamento semantico tra tutte le sezioni del profilo",
      },
      engagement: {
        label: "Engagement",
        description: "Potenziale di generare interazioni di valore per 360Brew",
      },
      pillars: {
        label: "Pillar Tematici",
        description: "I 2-4 temi chiave per massimizzare la tua reach",
      },
      services: {
        label: "Servizi",
        description: "Bio dei servizi e offerta core per il ranking diretto",
      },
      experience: {
        label: "Esperienza",
        description: "Analisi del ruolo e dei risultati per l'algoritmo Blue XL",
      },
    },
    brew: {
      title: "Cos'è 360Brew?",
      subtitle: "L'Algoritmo AI di LinkedIn Spiegato",
      intro: "360Brew è il foundation model di LinkedIn (parte di Microsoft) che ha rivoluzionato il ranking dei contenuti e dei profili. Passando dalle vanity metrics alla qualità semantica.",
      timeline: "Evoluzione di 360Brew",
      specs: "Specifiche Tecniche (Blue XL)",
      howItWorks: "Come Funziona",
      beforeAfter: "Prima vs Dopo 360Brew",
      bestPractices: "Best Practices",
      step1Title: "Promptizzazione Dati",
      step1Desc: "Ogni campo del profilo (Headline, About, Job Titles) viene trasformato in prompt testuali per l'LLM.",
      step2Title: "Mappatura Semantica",
      step2Desc: "Il modello mappa le tue keyword su uno spazio vettoriale di 1536 dimensioni per trovare coerenza.",
      step3Title: "Ranking Context-Aware",
      step3Desc: "La tua reach dipende da quanto il tuo 'semantic profile' è rilevante per gli interessi del target.",
      paperTitle: "Approfondisci lo studio su 360Brew",
      paperDesc: "Leggi il paper scientifico del team FAIT su arXiv per i dettagli tecnici sull'unificazione di ranking e raccomandazione.",
      paperLink: "Leggi su arXiv.org",
    },
    giovanni: {
      title: "Giovanni Burdi",
      subtitle: "B2B SaaS Growth Marketer | The ROI Architect™",
      description: "Aiuto aziende B2B SaaS, founder e team enterprise a scalare la crescita con Technical SEO, content systems e brand architecture.",
      expertise: "Aree di Expertise",
      resources: "Risorse & Canali",
      cta: "Ottimizza il tuo profilo con 360Brew",
      ctaDesc: "Giovanni ha creato questo tool per aiutarti a massimizzare la visibilità su LinkedIn secondo l'algoritmo AI più avanzato del settore.",
    },
    roi: {
      title: "The ROI Architect™",
      subtitle: "Sistemi di Crescita Scalabili per B2B SaaS",
      description: "The ROI Architect™ è il brand e la company di Giovanni Burdi dedicata a framework, metodologie e risorse per costruire sistemi di crescita misurabili e scalabili.",
      areas: "Le Aree di Competenza",
      philosophy: "La Filosofia ROI Architect",
      service1Title: "ROI Architecture",
      service1Desc: "Sistemi di misurazione e ottimizzazione del ritorno sull'investimento per ogni canale marketing.",
      service2Title: "GTM Strategy",
      service2Desc: "Go-to-market strategy basata su dati, segmentazione precisa e posizionamento competitivo.",
      service3Title: "Growth Systems",
      service3Desc: "Architettura di sistemi di crescita scalabili per SaaS B2B con focus su retention e expansion.",
      service4Title: "AI Integration",
      service4Desc: "Integrazione di AI e automazione nei processi di marketing e sales per massimizzare l'efficienza.",
      heroBadge: "Scaliamo ciò che conta",
      philosophyQuote: "\"Non scalare ciò che è rotto. Architetta ciò che conta. La crescita sostenibile è un processo sistematico, non una serie di growth hacks.\"",
      philosophyDesc: "The ROI Architect™ aiuta le aziende B2B SaaS a passare da \"atti casuali di marketing\" a una architettura di crescita coesa e data-driven che offre un ROI prevedibile.",
      philosophyBtn: "Esplora i Framework su LinkedIn",
    },
    viewpoint: {
      title: "The Viewpoint",
      subtitle: "La newsletter LinkedIn di Giovanni Burdi",
      description: "The Viewpoint è la newsletter LinkedIn di Giovanni Burdi con analisi settimanali su AI, marketing e strategie di crescita B2B.",
      topics: "Di cosa parla The Viewpoint?",
      whySubscribe: "Perché iscriversi?",
      subscribeBtn: "Iscriviti a The Viewpoint",
    },
    ridmix: {
      title: "Ridmix",
      subtitle: "Electronic Music Producer — Ridouan Labyad",
      description: "RIDMIX è l'identità artistica di Ridouan Labyad — systems thinker, global storyteller e produttore di musica elettronica.",
      aboutTitle: "Chi è Ridmix?",
      genres: "Generi Musicali",
      whereToFind: "Dove Trovare Ridmix",
    },
    studio: {
      title: "Creative Studio",
      subtitle: "AI Visual & Semantic Assets",
      description: "Accedi allo studio creativo AI per generare asset visivi e metadati semantici per il tuo profilo LinkedIn.",
    },
    footer: {
      optimizer: "LinkedIn Profile Optimizer",
      by: "by Giovanni Burdi",
      powered: "Powered by 360Brew Algorithm Intelligence",
    }
  },
  en: {
    common: {
      analysisFor: "Analysis for",
      viewProfile: "View LinkedIn Profile",
      copy: "Copy",
      openNewTab: "Open in New Tab",
      poweredBy: "Embedded AI Studio powered by Giovanni Burdi",
      connectLinkedIn: "Connect on LinkedIn",
      followLinkedIn: "Follow on LinkedIn",
      subscribeLinkedIn: "Subscribe on LinkedIn",
      goNewsletter: "Go to LinkedIn Newsletter",
      openManual: "Read User Manual",
      downloadManual: "Download PDF",
      close: "Close",
      zoomIn: "Zoom In",
      zoomOut: "Zoom Out",
    },
    nav: {
      optimizer: "Optimizer",
      brew: "360Brew",
      giovanni: "Giovanni Burdi",
      roi: "The ROI Architect",
      viewpoint: "The Viewpoint",
      ridmix: "Ridmix Music",
      studio: "Creative Studio",
    },
    home: {
      title: "LinkedIn Profile",
      subtitle: "360Brew Optimizer",
      description: "Analyze your LinkedIn profile section by section according to the 360Brew AI algorithm. Get ready-to-copy suggestions to reach a 95%+ score.",
      inputTitle: "Enter your profile data",
      fullNameLabel: "Full Name",
      profileUrlLabel: "LinkedIn Profile Link",
      headlineLabel: "Headline",
      aboutLabel: "About Section",
      skillsLabel: "Skills",
      industryLabel: "Industry",
      positionLabel: "Current Role",
      experienceLabel: "Work Experience (Details)",
      experienceTitleLabel: "Job Title",
      experienceTypeLabel: "Employment Type",
      experienceCompanyLabel: "Company / Organization",
      experienceDescLabel: "Description",
      experienceSkillsLabel: "Skills (Utilized)",
      servicesBioLabel: "Services Bio (Max 500 chars)",
      servicesListLabel: "Services List (comma separated)",
      analyzeBtn: "Start 360Brew Analysis",
      analyzing: "Analyzing...",
      resetBtn: "Reset",
      overallScore: "360Brew Overall Score",
      copyBtn: "Copy Optimized Text",
      copied: "Copied!",
      analysisTitle: "Analysis",
      suggestionsTitle: "Suggestions for 95%+",
      optimizedTitle: "Optimized Text (Ready to Copy)",
      placeholderFullName: "E.g.: Giovanni Burdi",
      placeholderProfileUrl: "E.g.: https://www.linkedin.com/in/username",
      placeholderHeadline: "E.g.: B2B SaaS Growth Marketer | AI Strategy | GTM Expert | 10+ years",
      placeholderAbout: "Paste your LinkedIn About section here...",
      placeholderSkills: "E.g.: Marketing Strategy, SEO, AI, Leadership...",
      placeholderIndustry: "E.g.: Technology, Marketing, SaaS...",
      placeholderPosition: "E.g.: Growth Manager",
      placeholderExperienceTitle: "E.g.: Senior Growth Manager",
      placeholderExperienceType: "E.g.: Full-time, Freelance, Part-time",
      placeholderExperienceCompany: "E.g.: Tech Corp",
      placeholderExperienceDesc: "Paste role description here...",
      placeholderExperienceSkills: "E.g.: Technical SEO, AI, GTM Strategy...",
      placeholderServicesBio: "Describe your services in a persuasive and semantically dense way...",
      placeholderServicesList: "E.g.: Growth Strategy, Technical SEO, AI Automation...",
      errorInput: "Enter at least Headline and About to start analysis",
      successAnalysis: "Analysis complete! 360Brew Score: ",
      ctaTitle: "Want a deeper analysis?",
      ctaDesc: "Connect with Giovanni Burdi for advanced GTM strategies.",
      ctaBtn: "Connect on LinkedIn",
    },
    sections: {
      headline: {
        label: "Headline",
        description: "Your business card — the first thing 360Brew evaluates",
      },
      about: {
        label: "About",
        description: "Your professional storytelling and semantic narrative",
      },
      skills: {
        label: "Skills",
        description: "Declared skills and their thematic coherence",
      },
      coherence: {
        label: "Coherence",
        description: "Semantic alignment between all profile sections",
      },
      engagement: {
        label: "Engagement",
        description: "Potential to generate value interactions for 360Brew",
      },
      pillars: {
        label: "Content Pillars",
        description: "2-4 key themes to maximize your reach",
      },
      services: {
        label: "Services",
        description: "Services bio and core offering for direct ranking",
      },
      experience: {
        label: "Experience",
        description: "Role and results analysis for the Blue XL algorithm",
      },
    },
    brew: {
      title: "What is 360Brew?",
      subtitle: "The LinkedIn AI Algorithm Explained",
      intro: "360Brew is LinkedIn's foundation model (part of Microsoft) that revolutionized content and profile ranking. Moving from vanity metrics to semantic quality.",
      timeline: "360Brew Evolution",
      specs: "Technical Specs (Blue XL)",
      howItWorks: "How It Works",
      beforeAfter: "Before vs After 360Brew",
      bestPractices: "Best Practices",
      step1Title: "Data Prompting",
      step1Desc: "Every profile field (Headline, About, Job Titles) is transformed into text prompts for the LLM.",
      step2Title: "Semantic Mapping",
      step2Desc: "The model maps your keywords onto a 1536-dimensional vector space to find coherence.",
      step3Title: "Context-Aware Ranking",
      step3Desc: "Your reach depends on how relevant your 'semantic profile' is to the target's interests.",
      paperTitle: "Dive deeper into 360Brew",
      paperDesc: "Read the FAIT team's scientific paper on arXiv for technical details on unifying ranking and recommendation.",
      paperLink: "Read on arXiv.org",
    },
    giovanni: {
      title: "Giovanni Burdi",
      subtitle: "B2B SaaS Growth Marketer | The ROI Architect™",
      description: "I help B2B SaaS companies, founders, and enterprise teams scale growth with Technical SEO, content systems, and brand architecture.",
      expertise: "Areas of Expertise",
      resources: "Resources & Channels",
      cta: "Optimize your profile with 360Brew",
      ctaDesc: "Giovanni created this tool to help you maximize visibility on LinkedIn according to the industry's most advanced AI algorithm.",
    },
    roi: {
      title: "The ROI Architect™",
      subtitle: "Scalable Growth Systems for B2B SaaS",
      description: "The ROI Architect™ is Giovanni Burdi's brand and company dedicated to frameworks, methodologies, and resources for building measurable and scalable growth systems.",
      areas: "Areas of Expertise",
      philosophy: "The ROI Architect Philosophy",
      service1Title: "ROI Architecture",
      service1Desc: "Measurement systems and optimization of return on investment for every marketing channel.",
      service2Title: "GTM Strategy",
      service2Desc: "Data-driven go-to-market strategy, precise segmentation, and competitive positioning.",
      service3Title: "Growth Systems",
      service3Desc: "Architecture of scalable growth systems for B2B SaaS with a focus on retention and expansion.",
      service4Title: "AI Integration",
      service4Desc: "Integration of AI and automation into marketing and sales processes to maximize efficiency.",
      heroBadge: "Let's Scale What Matters",
      philosophyQuote: "\"Don't scale what's broken. Architect what matters. Sustainable growth is a systematic process, not a series of growth hacks.\"",
      philosophyDesc: "The ROI Architect™ helps B2B SaaS companies transition from \"random acts of marketing\" to a cohesive, data-driven growth architecture that delivers predictable ROI.",
      philosophyBtn: "Explore Frameworks on LinkedIn",
    },
    viewpoint: {
      title: "The Viewpoint",
      subtitle: "Giovanni Burdi's LinkedIn Newsletter",
      description: "The Viewpoint is Giovanni Burdi's LinkedIn newsletter with weekly analyses on AI, marketing, and B2B growth strategies.",
      topics: "What is The Viewpoint about?",
      whySubscribe: "Why subscribe?",
      subscribeBtn: "Subscribe to The Viewpoint",
      heroBadge: "Expert Perspectives & Insights",
      aboutTitle: "What is The Viewpoint?",
      aboutText1: "The Viewpoint is a weekly newsletter curated by Giovanni Burdi, designed for those who want to stay at the forefront of the world of B2B marketing, AI, and growth systems.",
      aboutText2: "Each edition offers a deep and analytical perspective on today's market challenges, providing practical frameworks and exclusive insights derived from Giovanni's direct experience as an ROI Architect.",
      aboutText3: "Join a community of professionals and industry leaders who have chosen to scale what really matters, based on data and successful architectures.",
      joinTitle: "Join the Community",
      joinDesc: "Receive the best strategies every week to scale your B2B business.",
    },
    ridmix: {
      title: "Ridmix",
      subtitle: "Electronic Music Producer — Ridouan Labyad",
      description: "RIDMIX is the artistic identity of Ridouan Labyad — systems thinker, global storyteller, and electronic music producer.",
      aboutTitle: "Who is Ridmix?",
      genres: "Music Genres",
      whereToFind: "Where to find Ridmix",
    },
    studio: {
      title: "Creative Studio",
      subtitle: "AI Visual & Semantic Assets",
      description: "Access the AI Creative Studio to generate visual assets and semantic metadata for your LinkedIn profile.",
    },
    footer: {
      optimizer: "LinkedIn Profile Optimizer",
      by: "by Giovanni Burdi",
      powered: "Powered by 360Brew Algorithm Intelligence",
    }
  },
  de: {
    common: {
      analysisFor: "Analyse für",
      viewProfile: "LinkedIn-Profil ansehen",
      copy: "Kopieren",
      openNewTab: "In neuem Tab öffnen",
      poweredBy: "Embedded AI Studio powered by Giovanni Burdi",
      connectLinkedIn: "Auf LinkedIn vernetzen",
      followLinkedIn: "Auf LinkedIn folgen",
      subscribeLinkedIn: "Auf LinkedIn abonnieren",
      goNewsletter: "Zum LinkedIn-Newsletter",
      openManual: "Benutzerhandbuch lesen",
      downloadManual: "PDF herunterladen",
      close: "Schließen",
      zoomIn: "Vergrößern",
      zoomOut: "Verkleinern",
    },
    nav: {
      optimizer: "Optimierer",
      brew: "360Brew",
      giovanni: "Giovanni Burdi",
      roi: "The ROI Architect",
      viewpoint: "The Viewpoint",
      ridmix: "Ridmix Music",
      studio: "Creative Studio",
    },
    home: {
      title: "LinkedIn Profil",
      subtitle: "360Brew Optimierer",
      description: "Analysieren Sie Ihr LinkedIn-Profil Abschnitt für Abschnitt nach dem 360Brew AI-Algorithmus. Erhalten Sie kopierfertige Vorschläge, um eine Punktzahl von 95 %+ zu erreichen.",
      inputTitle: "Geben Sie Ihre Profildaten ein",
      fullNameLabel: "Vollständiger Name",
      profileUrlLabel: "LinkedIn-Profil-Link",
      headlineLabel: "Headline",
      aboutLabel: "Info-Bereich",
      skillsLabel: "Kenntnisse",
      industryLabel: "Branche",
      positionLabel: "Aktuelle Rolle",
      experienceLabel: "Berufserfahrung (Details)",
      experienceTitleLabel: "Jobtitel",
      experienceTypeLabel: "Beschäftigungsart",
      experienceCompanyLabel: "Unternehmen / Organisation",
      experienceDescLabel: "Beschreibung",
      experienceSkillsLabel: "Fähigkeiten (Genutzt)",
      servicesBioLabel: "Dienstleistungs-Bio (Max. 500 Zeichen)",
      servicesListLabel: "Dienstleistungsliste (kommagetrennt)",
      analyzeBtn: "360Brew Analyse starten",
      analyzing: "Analysieren...",
      resetBtn: "Zurücksetzen",
      overallScore: "360Brew Gesamtpunktzahl",
      copyBtn: "Optimierten Text kopieren",
      copied: "Kopiert!",
      analysisTitle: "Analyse",
      suggestionsTitle: "Vorschläge für 95%+",
      optimizedTitle: "Optimierter Text (Kopierfertig)",
      placeholderFullName: "Z.B.: Giovanni Burdi",
      placeholderProfileUrl: "Z.B.: https://www.linkedin.com/in/username",
      placeholderHeadline: "Z.B.: B2B SaaS Growth Marketer | KI-Strategie | GTM-Experte | 10+ Jahre",
      placeholderAbout: "Fügen Sie hier Ihren LinkedIn-Info-Abschnitt ein...",
      placeholderSkills: "Z.B.: Marketingstrategie, SEO, KI, Führung...",
      placeholderIndustry: "Z.B.: Technologie, Marketing, SaaS...",
      placeholderPosition: "Z.B.: Growth Manager",
      placeholderExperienceTitle: "Z.B.: Senior Growth Manager",
      placeholderExperienceType: "Z.B.: Vollzeit, Freiberuflich, Teilzeit",
      placeholderExperienceCompany: "Z.B.: Tech Corp",
      placeholderExperienceDesc: "Stellenbeschreibung hier einfügen...",
      placeholderExperienceSkills: "Z.B.: Technical SEO, AI, GTM Strategy...",
      placeholderServicesBio: "Beschreiben Sie Ihre Dienstleistungen überzeugend und semantisch dicht...",
      placeholderServicesList: "Z.B.: Growth Strategy, Technical SEO, AI Automation...",
      errorInput: "Geben Sie mindestens Headline und Info ein, um die Analyse zu starten",
      successAnalysis: "Analyse abgeschlossen! 360Brew Score: ",
      ctaTitle: "Möchten Sie eine tiefere Analyse?",
      ctaDesc: "Vernetzen Sie sich mit Giovanni Burdi für fortgeschrittene GTM-Strategien.",
      ctaBtn: "Auf LinkedIn vernetzen",
    },
    sections: {
      headline: {
        label: "Headline",
        description: "Ihre Visitenkarte — das Erste, was 360Brew bewertet",
      },
      about: {
        label: "Info",
        description: "Ihr professionelles Storytelling und Ihre semantische Erzählung",
      },
      skills: {
        label: "Kenntnisse",
        description: "Deklarierte Fähigkeiten und deren thematische Kohärenz",
      },
      coherence: {
        label: "Kohärenz",
        description: "Semantische Ausrichtung zwischen allen Profilabschnitten",
      },
      engagement: {
        label: "Engagement",
        description: "Potenzial zur Generierung von Wertinteraktionen für 360Brew",
      },
      pillars: {
        label: "Inhaltssäulen",
        description: "2-4 Schlüsselthemen zur Maximierung Ihrer Reichweite",
      },
      services: {
        label: "Dienstleistungen",
        description: "Dienstleistungs-Bio und Kernangebot für direktes Ranking",
      },
      experience: {
        label: "Erfahrung",
        description: "Rollen- und Ergebnisanalyse für den Blue XL-Algorithmus",
      },
    },
    brew: {
      title: "Was ist 360Brew?",
      subtitle: "Der LinkedIn KI-Algorithmus erklärt",
      intro: "360Brew ist das Basismodell von LinkedIn (Teil von Microsoft), das das Ranking von Inhalten und Profilen revolutioniert hat. Weg von Vanity Metrics hin zu semantischer Qualität.",
      timeline: "360Brew Entwicklung",
      specs: "Technische Daten (Blue XL)",
      howItWorks: "Wie es funktioniert",
      beforeAfter: "Vorher vs. Nachher 360Brew",
      bestPractices: "Best Practices",
      step1Title: "Daten-Prompting",
      step1Desc: "Jedes Profilfeld (Headline, Info, Jobtitel) wird in Text-Prompts für das LLM umgewandelt.",
      step2Title: "Semantisches Mapping",
      step2Desc: "Das Modell bildet Ihre Keywords in einem 1536-dimensionalen Vektorraum ab, um Kohärenz zu finden.",
      step3Title: "Kontextsensitives Ranking",
      step3Desc: "Ihre Reichweite hängt davon ab, wie relevant Ihr 'semantisches Profil' für die Interessen der Zielgruppe ist.",
      paperTitle: "Tauchen Sie tiefer in 360Brew ein",
      paperDesc: "Lesen Sie das wissenschaftliche Paper des FAIT-Teams auf arXiv für technische Details zur Vereinheitlichung von Ranking und Empfehlung.",
      paperLink: "Auf arXiv.org lesen",
    },
    giovanni: {
      title: "Giovanni Burdi",
      subtitle: "B2B SaaS Growth Marketer | The ROI Architect™",
      description: "Ich helfe B2B SaaS-Unternehmen, Gründern und Enterprise-Teams, das Wachstum mit technischem SEO, Content-Systemen und Markenarchitektur zu skalieren.",
      expertise: "Fachgebiete",
      resources: "Ressourcen & Kanäle",
      cta: "Optimieren Sie Ihr Profil mit 360Brew",
      ctaDesc: "Giovanni hat dieses Tool entwickelt, um Ihnen zu helfen, die Sichtbarkeit auf LinkedIn gemäß dem fortschrittlichsten KI-Algorithmus der Branche zu maximieren.",
    },
    roi: {
      title: "The ROI Architect™",
      subtitle: "Skalierbare Wachstumssysteme für B2B SaaS",
      description: "The ROI Architect™ ist die Marke und das Unternehmen von Giovanni Burdi, das sich Frameworks, Methoden und Ressourcen für den Aufbau messbarer und skalierbarer Wachstumssysteme widmet.",
      areas: "Fachgebiete",
      philosophy: "Die ROI Architect Philosophie",
      service1Title: "ROI-Architektur",
      service1Desc: "Messsysteme und Optimierung des Return on Investment für jeden Marketingkanal.",
      service2Title: "GTM-Strategie",
      service2Desc: "Datengesteuerte Go-to-Market-Strategie, präzise Segmentierung und wettbewerbsfähige Positionierung.",
      service3Title: "Wachstumssysteme",
      service3Desc: "Architektur skalierbarer Wachstumssysteme für B2B-SaaS mit Fokus auf Retention und Expansion.",
      service4Title: "KI-Integration",
      service4Desc: "Integration von KI und Automatisierung in Marketing- und Vertriebsprozesse zur Maximierung der Effizienz.",
      heroBadge: "Skalieren wir, was zählt",
      philosophyQuote: "\"Skaliere nicht, was kaputt ist. Architektiere, was zählt. Nachhaltiges Wachstum ist ein systematischer Prozess, keine Serie von Growth Hacks.\"",
      philosophyDesc: "The ROI Architect™ hilft B2B-SaaS-Unternehmen beim Übergang von \"zufälligen Marketing-Aktionen\" zu einer kohärenten, datengesteuerten Wachstumsarchitektur, die einen vorhersehbaren ROI liefert.",
      philosophyBtn: "Frameworks auf LinkedIn erkunden",
    },
    viewpoint: {
      title: "The Viewpoint",
      subtitle: "Giovanni Burdis LinkedIn-Newsletter",
      description: "The Viewpoint ist Giovanni Burdis LinkedIn-Newsletter mit wöchentlichen Analysen zu KI, Marketingstrategien und B2B-Wachstum.",
      topics: "Worum geht es in The Viewpoint?",
      whySubscribe: "Warum abonnieren?",
      subscribeBtn: "The Viewpoint abonnieren",
      heroBadge: "Experten-Perspektiven & Einblicke",
      aboutTitle: "Was ist The Viewpoint?",
      aboutText1: "The Viewpoint ist ein wöchentlicher Newsletter, der von Giovanni Burdi kuratiert wird und für diejenigen gedacht ist, die an der Spitze der Welt des B2B-Marketings, der KI und der Wachstumssysteme bleiben wollen.",
      aboutText2: "Jede Ausgabe bietet eine tiefe und analytische Perspektive auf die heutigen Marktherausforderungen und bietet praktische Frameworks und exklusive Einblicke, die aus Giovannis direkter Erfahrung als ROI Architect stammen.",
      aboutText3: "Schließen Sie sich einer Community von Fachleuten und Branchenführern an, die sich entschieden haben, das zu skalieren, was wirklich zählt, basierend auf Daten und erfolgreichen Architekturen.",
      joinTitle: "Tritt der Community bei",
      joinDesc: "Erhalten Sie jede Woche die besten Strategien, um Ihr B2B-Geschäft zu skalieren.",
    },
    ridmix: {
      title: "Ridmix",
      subtitle: "Produzent elektronischer Musik — Ridouan Labyad",
      description: "RIDMIX ist die künstlerische Identität von Ridouan Labyad — Systemdenker, globaler Geschichtenerzähler und Produzent elektronischer Musik.",
      aboutTitle: "Wer ist Ridmix?",
      genres: "Musikrichtungen",
      whereToFind: "Wo man Ridmix findet",
    },
    studio: {
      title: "Creative Studio",
      subtitle: "AI Visual & Semantic Assets",
      description: "Greifen Sie auf das AI Creative Studio zu, um visuelle Assets und semantische Metadaten für Ihr LinkedIn-Profil zu generieren.",
    },
    footer: {
      optimizer: "LinkedIn Profil Optimierer",
      by: "von Giovanni Burdi",
      powered: "Unterstützt von 360Brew Algorithm Intelligence",
    }
  },
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem("app_lang");
    return (saved as Language) || "it";
  });

  useEffect(() => {
    localStorage.setItem("app_lang", language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (path: string) => {
    const keys = path.split(".");
    let current: any = TRANSLATIONS[language];
    for (const key of keys) {
      if (current[key] === undefined) return path;
      current = current[key];
    }
    return current;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
